import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect, useRef } from 'react'
import { z } from 'zod'
import { useEventById, useEventReminder, useToggleEventReminder } from '@/entities/event'
import { getSpotsLeft, getComputedStatus } from '@/entities/event/lib/helpers'
import { AppShell } from '@/shared/ui/app-shell'
import { BottomNav } from '@/widgets/bottom-nav'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { ApplicationForm } from '@/features/applications'
import { EventFloatingHeader } from '@/widgets/event-floating-header'
import { ProductHeroGallery } from '@/widgets/product-hero-gallery'
import { EventIdentityBlock } from '@/widgets/event-identity-block'
import { ProductDescription } from '@/widgets/product-description'
import { EventActionBar } from '@/widgets/event-action-bar'
import { Skeleton } from '@/shared/ui/skeleton'
import { ToastContainer, useToast } from '@/shared/ui/toast'
import { useTelegram, useBackButton } from '@/shared/lib/telegram'
import { requireAuth } from '@/shared/lib/router/guards'

const eventParamsSchema = z.object({
  eventId: z.string(),
})

const eventSearchSchema = z.object({
  apply: z.preprocess((value) => {
    if (value === '1' || value === 'true' || value === 1 || value === true) return true
    return undefined
  }, z.boolean().optional()),
})

export const Route = createFileRoute('/event/$eventId')({
  params: {
    parse: (params) => eventParamsSchema.parse(params),
    stringify: (params) => ({ eventId: params.eventId }),
  },
  validateSearch: eventSearchSchema,
  component: EventPage,
})

function EventPage() {
  const { eventId } = Route.useParams()
  const { apply } = Route.useSearch()
  const navigate = useNavigate()
  const { initData, hapticFeedback } = useTelegram()
  
  // Нативная кнопка "Назад" в заголовке Telegram
  useBackButton(true)
  
  const { data, isLoading } = useEventById(eventId)
  const [isApplicationFormOpen, setIsApplicationFormOpen] = useState(false)
  const [isApplied, setIsApplied] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const autoApplyOpenedRef = useRef(false)
  const { toasts, success, remove } = useToast()
  const event = data?.data

  // Скролл наверх при открытии страницы события
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 })
    autoApplyOpenedRef.current = false
  }, [eventId, apply, event?.id])

  const spotsLeft = event ? getSpotsLeft(event) : null
  const isFull = spotsLeft !== null && spotsLeft <= 0
  
  // Проверка статуса события
  const eventStatus = event ? getComputedStatus(event) : null
  const isPast = eventStatus === 'completed'
  const isCancelled = eventStatus === 'cancelled'
  const isRegistrationClosed = isPast || isCancelled || isFull

  useEffect(() => {
    if (!apply || autoApplyOpenedRef.current || !event || !initData || isRegistrationClosed || isApplied || isApplicationFormOpen) return
    autoApplyOpenedRef.current = true
    setIsApplicationFormOpen(true)
  }, [apply, event, initData, isApplicationFormOpen, isApplied, isRegistrationClosed])

  // Загрузка
  if (isLoading) {
    return (
      <AppShell showBottomNav={false}>
        <div className="min-h-screen bg-paper dark:bg-[#0F1113]">
          <Skeleton className="w-full" style={{ height: '65vh' }} />
          <div className="px-4 py-6 space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
        <BottomNav />
      </AppShell>
    )
  }

  // Ошибка или событие не найдено
  if (!event) {
    return (
      <AppShell showBottomNav>
        <div className="min-h-screen bg-paper dark:bg-[#0F1113] flex items-center justify-center px-4">
          <div className="text-center space-y-4">
            <h1 className="text-2xl font-bold text-ink dark:text-white">Событие не найдено</h1>
            <p className="text-gray-500 dark:text-gray-400">Событие с таким ID не существует или было удалено</p>
            <button
              onClick={() => navigate({ to: '/events' })}
              className="px-6 py-3 bg-primary dark:bg-primary-dark text-white rounded-2xl font-medium"
            >
              Вернуться к событиям
            </button>
          </div>
        </div>
        <BottomNav />
      </AppShell>
    )
  }

  // Получаем изображения
  const images = Array.isArray(event.images) 
    ? event.images 
    : typeof event.images === 'string' 
      ? [event.images] 
      : []

  // Обработка записи на событие
  const handleApply = () => {
    if (!initData) {
      requireAuth(initData)
      return
    }
    hapticFeedback.impactOccurred('medium')
    setIsApplicationFormOpen(true)
  }

  // Обработка успешной отправки заявки
  const handleApplicationSuccess = () => {
    setIsApplicationFormOpen(false)
    setIsApplied(true)
    success('Заявка отправлена')
    hapticFeedback.notificationOccurred('success')
  }

  return (
    <AppShell showBottomNav={false}>
      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-paper dark:bg-[#0F1113]"
      >
        {/* Floating Header */}
        <EventFloatingHeader eventName={event.title} eventId={event.id} />
        
        {/* Hero Gallery */}
        <ProductHeroGallery
          images={images}
          height={65}
        />
        
        {/* Identity Block */}
        <EventIdentityBlock event={event} />

        {/* Reminder Toggle */}
        {initData && !isPast && !isCancelled && (
          <EventReminderSection eventId={event.id} />
        )}
        
        {/* Description */}
        {event.description && (
          <ProductDescription description={event.description} />
        )}
        
        {/* Отступ снизу для Action Bar и Bottom Nav */}
        <div 
          aria-hidden="true"
          style={{ 
            height: 'calc(96px + var(--bottom-nav-height, 0px) + env(safe-area-inset-bottom, 0px))',
          }} 
        />
      </div>
      
      {/* Fixed Action Bar */}
      <EventActionBar
        price={event.isFree ? 0 : event.price}
        isFree={event.isFree}
        isApplied={isApplied}
        loading={false}
        disabled={isRegistrationClosed}
        disabledReason={isPast ? 'past' : isCancelled ? 'cancelled' : isFull ? 'full' : undefined}
        onApply={handleApply}
      />
      
      {/* Bottom Sheet для формы заявки */}
      <BottomSheet
        isOpen={isApplicationFormOpen}
        onClose={() => {
          hapticFeedback.impactOccurred('light')
          setIsApplicationFormOpen(false)
        }}
        title="Запись на событие"
        height={80}
        onHapticFeedback={(type) => {
          if (type === 'light') hapticFeedback.impactOccurred('light')
          else if (type === 'selection') hapticFeedback.selectionChanged()
        }}
      >
        <div className="px-6 py-4">
          <ApplicationForm
            eventId={eventId}
            eventTitle={event?.title || 'Событие'}
            maxGuests={spotsLeft || 20}
            onSuccess={handleApplicationSuccess}
            onCancel={() => {
              hapticFeedback.impactOccurred('light')
              setIsApplicationFormOpen(false)
            }}
          />
        </div>
      </BottomSheet>
      
      {/* Toast уведомления */}
      <ToastContainer
        toasts={toasts}
        onRemove={remove}
        onHapticFeedback={(type) => {
          if (type === 'success' || type === 'error' || type === 'warning') {
            hapticFeedback.notificationOccurred(type)
          }
        }}
      />
      
      {/* Bottom Navigation */}
      <BottomNav />
    </AppShell>
  )
}

function EventReminderSection({ eventId }: { eventId: string }) {
  const { data: reminderState, isLoading } = useEventReminder(eventId)
  const toggleMutation = useToggleEventReminder(eventId)
  const { hapticFeedback } = useTelegram()

  const handleToggle = () => {
    if (isLoading || toggleMutation.isPending) return

    const action = reminderState?.enabled ? 'disable' : 'enable'
    hapticFeedback.impactOccurred('medium')
    toggleMutation.mutate(action)
  }

  if (isLoading) {
    return (
      <div className="mx-4 my-3 p-4 bg-gray-50 dark:bg-[#1C1E22] rounded-2xl animate-pulse flex justify-between items-center">
        <div className="space-y-2">
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-3 w-48 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
        <div className="h-6 w-11 bg-gray-200 dark:bg-gray-700 rounded-full" />
      </div>
    )
  }

  const isEnabled = !!reminderState?.enabled

  return (
    <div className="mx-4 my-3 p-4 bg-gray-50 dark:bg-[#1C1E22] rounded-2xl border border-gray-100 dark:border-gray-800 flex justify-between items-center shadow-sm transition-all duration-300">
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-primary/10 dark:bg-primary-dark/10 rounded-xl text-primary dark:text-primary-dark">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-ink dark:text-white">Напомнить о событии</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">Уведомление в Telegram за 24 часа</p>
        </div>
      </div>
      
      <button
        onClick={handleToggle}
        disabled={toggleMutation.isPending}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${
          isEnabled ? 'bg-primary dark:bg-primary-dark' : 'bg-gray-300 dark:bg-gray-700'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
            isEnabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  )
}
